/**
 * `FLY A HISTORIC MISSION` — the two popups the original puts between the grid
 * button and the briefing.
 *
 * `PS.EXE`'s own strings for this flow sit together in one run of its data
 * segment, which is how the shape was recovered: `FLY A HISTORIC MISSION`,
 * `SELECT HISTORIC MISSION:`, then `BRITISH` and `GERMAN` eight bytes apart,
 * and — a few dozen bytes earlier — `You cannot play the %b side of this
 * mission.` next to its `OK`. So the flow is: pick one of the eight, pick a
 * side, and be told by name when the side you picked is not one the encounter
 * has (docs/formats/historic-dat.md § *The picker*).
 *
 * The original draws the middle step as a full list screen with that prompt
 * above a well. This port draws it as the same popup its sibling pickers use
 * (`sidePickerDialog`, `parameterDialog`), because the shell's list-screen
 * widget belongs to the career surface and the encounter list is nine rows —
 * the wording is the original's, the frame is this shell's.
 */

import { HISTORIC_ENCOUNTERS, historicSeatPlayable, historicSeatRefusal, type Nation } from "@red-opus/engine";
import { dialogRow, noticeDialog, type ShellDialog } from "./shell-screen.ts";

/** `PS.EXE`'s own prompt, verbatim. */
export const HISTORIC_PROMPT = "SELECT HISTORIC MISSION:";

/** The command a picked encounter carries, e.g. `historic:historic-4`. */
export const HISTORIC_COMMAND_PREFIX = "historic:";

/** The eight, in the file's order, which is the order the original lists them in. */
export function historicPickerDialog(): ShellDialog {
  return {
    title: HISTORIC_PROMPT,
    body: [],
    rows: [
      ...HISTORIC_ENCOUNTERS.map((data) =>
        dialogRow(data.id, data.title, { kind: "command", command: `${HISTORIC_COMMAND_PREFIX}${data.id}` }),
      ),
      dialogRow("cancel", "CANCEL", { kind: "close" }),
    ],
    focus: 0,
  };
}

/** The encounter a `historic:` command names, or nothing when it names none. */
export function historicCommandTarget(command: string): string | undefined {
  if (!command.startsWith(HISTORIC_COMMAND_PREFIX)) return undefined;
  const id = command.slice(HISTORIC_COMMAND_PREFIX.length);
  return HISTORIC_ENCOUNTERS.some((data) => data.id === id) ? id : undefined;
}

/**
 * The side popup for one encounter: the same two rows and emblems the rest of
 * the shell uses, minus whichever seat the file writes `FF FF FF` for.
 */
export function historicSideDialog(id: string): ShellDialog {
  const rows = [
    { nation: "german" as Nation, label: "German Air Service", emblem: "cross" as const },
    { nation: "allied" as Nation, label: "Royal Flying Corps", emblem: "roundel" as const },
  ]
    .filter((row) => historicSeatPlayable(id, row.nation))
    .map((row) => dialogRow(row.nation, row.label, { kind: "command", command: `side:${row.nation}` }, row.emblem));
  return { title: "WHO WILL YOU FLY FOR?", body: [], rows: [...rows, dialogRow("cancel", "CANCEL", { kind: "close" })], focus: 0 };
}

/** `PS.EXE`'s refusal, for a seat the encounter does not have. */
export function historicRefusalDialog(nation: Nation): ShellDialog {
  return noticeDialog(HISTORIC_PROMPT, [historicSeatRefusal(nation).toUpperCase()]);
}
